import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";

import type { Locale } from "@/i18n/config";

export interface WorkspaceMember {
  id: string;
  email: string;
  full_name: string | null;
  role: string; // "you", "member", "pending"
  status: string; // "active" or "pending"
}

export interface WorkspaceInfo {
  workspace_name: string;
  company_name: string | null;
  workspace_status: string;
  workspace_logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  cc_invirtus_support_enabled: boolean;
  default_language: Locale;
  plan_name: string | null;
  plan_expired: boolean;
  plan_expires_at: string | null;
  cv_limit: number;
  cv_used: number;
  interview_limit: number;
  interview_used: number;
  members: WorkspaceMember[];
  total_members: number;
}

export interface WorkspaceLanguageResponse {
  default_language: Locale;
}

export interface WorkspaceLogoResponse {
  workspace_logo_url: string | null;
}

export interface WorkspaceBrandingResponse {
  workspace_name: string;
  company_name: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  cc_invirtus_support_enabled: boolean;
}

export const useWorkspaceService = () => {
  const { getAccessTokenSilently } = useAuth0();
  const apiUrl = import.meta.env.VITE_API_SERVER_URL;

  const getAuthHeader = useCallback(async () => {
    const token = await getAccessTokenSilently({
      authorizationParams: {
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      },
    });
    return { Authorization: `Bearer ${token}` };
  }, [getAccessTokenSilently]);

  const getWorkspaceMembers = useCallback(async (): Promise<WorkspaceInfo> => {
    const authHeader = await getAuthHeader();
    const response = await fetch(`${apiUrl}/api/workspaces/members`, {
      method: "GET",
      headers: { ...authHeader },
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail ?? "Failed to load workspace");
    }
    return response.json();
  }, [apiUrl, getAuthHeader]);

  const inviteMember = useCallback(
    async (email: string): Promise<void> => {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${apiUrl}/api/workspaces/members/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail ?? errorData.message ?? "Failed to invite member");
      }
    },
    [apiUrl, getAuthHeader]
  );

  const setWorkspaceLanguage = useCallback(
    async (language: Locale): Promise<WorkspaceLanguageResponse> => {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${apiUrl}/api/workspaces/settings/language`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ language }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail ?? "Failed to update workspace language");
      }
      return response.json();
    },
    [apiUrl, getAuthHeader],
  );

  const setWorkspaceLogo = useCallback(
    async (file: File): Promise<WorkspaceLogoResponse> => {
      const authHeader = await getAuthHeader();
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${apiUrl}/api/workspaces/settings/logo`, {
        method: "POST",
        headers: { ...authHeader },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail ?? "Failed to update workspace logo");
      }

      return response.json();
    },
    [apiUrl, getAuthHeader],
  );

  const setWorkspaceBranding = useCallback(
    async (
      payload: {
        name: string;
        company_name: string | null;
        primary_color: string | null;
        secondary_color: string | null;
        cc_invirtus_support_enabled: boolean;
      }
    ): Promise<WorkspaceBrandingResponse> => {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${apiUrl}/api/workspaces/settings/branding`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail ?? "Failed to update workspace branding");
      }

      return response.json();
    },
    [apiUrl, getAuthHeader],
  );

  return {
    getWorkspaceMembers,
    inviteMember,
    setWorkspaceLanguage,
    setWorkspaceLogo,
    setWorkspaceBranding,
  };
};
